import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { SET_REPORT_DATA } from '../store/actions';
import axiosInstance from '../axiosInstance';
import configData from '../config';

// ─── KPI LIBRARY ─────────────────────────────────────────────────────────────
const KPI_LIBRARY = [
  { code: 'TOTAL_REVENUE',       name: 'Revenue',          category: 'Sales',         icon: '💰', color: '#3b82f6', format: 'currency', good: 'up'   },
  { code: 'GROSS_PROFIT',        name: 'Gross Profit',      category: 'Profitability', icon: '📈', color: '#10b981', format: 'currency', good: 'up'   },
  { code: 'NET_PROFIT',          name: 'Net Profit',        category: 'Profitability', icon: '✅', color: '#06b6d4', format: 'currency', good: 'up'   },
  { code: 'GROSS_MARGIN_PCT',    name: 'Gross Margin %',    category: 'Profitability', icon: '📊', color: '#8b5cf6', format: 'percent',  good: 'up'   },
  { code: 'NET_MARGIN_PCT',      name: 'Net Margin %',      category: 'Profitability', icon: '🎯', color: '#ec4899', format: 'percent',  good: 'up'   },
  { code: 'TOTAL_COST_OF_SALES', name: 'Cost of Sales',     category: 'Cost',          icon: '🏭', color: '#f59e0b', format: 'currency', good: 'down' },
  { code: 'PRODUCTION_QTY',      name: 'Production Qty',    category: 'Production',    icon: '⚙️', color: '#64748b', format: 'number',   good: 'up'   },
  { code: 'RECEIVABLES',         name: 'Receivables',       category: 'Cashflow',      icon: '💳', color: '#0ea5e9', format: 'currency', good: 'down' },
  { code: 'WORKING_CAPITAL',     name: 'Working Capital',   category: 'Cashflow',      icon: '💧', color: '#14b8a6', format: 'currency', good: 'up'   },
  { code: 'TOTAL_SALARY',        name: 'Employee Cost',     category: 'HR',            icon: '👥', color: '#a855f7', format: 'currency', good: 'down' },
  { code: 'FINISHED_GOODS',      name: 'Finished Goods',    category: 'Inventory',     icon: '📦', color: '#f97316', format: 'currency', good: 'up'   },
  { code: 'RAW_MATERIAL_VALUE',  name: 'Raw Material',      category: 'Inventory',     icon: '🪵', color: '#84cc16', format: 'currency', good: 'up'   },
  { code: 'PRODUCTION_LOSS',     name: 'Production Loss',   category: 'Production',    icon: '⚠️', color: '#ef4444', format: 'currency', good: 'down' },
  { code: 'DEALER_SATISFACTION', name: 'Dealer Score',      category: 'Sales',         icon: '🤝', color: '#06b6d4', format: 'ratio',    good: 'up'   },
  { code: 'PURCHASE_VALUE',      name: 'Purchase Value',    category: 'Cost',          icon: '🛒', color: '#f59e0b', format: 'currency', good: 'down' },
];

// ─── MOCK DATA — replace with real API per kpi_code ──────────────────────────
const MOCK_DATA = {
  TOTAL_REVENUE:       { value: 212000000, prev: 195800000 },
  GROSS_PROFIT:        { value: 4786172,   prev: 4551000   },
  NET_PROFIT:          { value: 3200000,   prev: 2980000   },
  GROSS_MARGIN_PCT:    { value: 34.2,      prev: 32.8      },
  NET_MARGIN_PCT:      { value: 22.6,      prev: 21.1      },
  TOTAL_COST_OF_SALES: { value: 65952461,  prev: 63100000  },
  PRODUCTION_QTY:      { value: 14485,     prev: 14200     },
  RECEIVABLES:         { value: 158711,    prev: 172000    },
  WORKING_CAPITAL:     { value: 50000000,  prev: 47500000  },
  TOTAL_SALARY:        { value: 15510,     prev: 14800     },
  FINISHED_GOODS:      { value: 38200000,  prev: 36100000  },
  RAW_MATERIAL_VALUE:  { value: 27700000,  prev: 25900000  },
  PRODUCTION_LOSS:     { value: 21000,     prev: 18500     },
  DEALER_SATISFACTION: { value: 4.2,       prev: 4.0       },
  PURCHASE_VALUE:      { value: 2660000,   prev: 2530000   },
};

// ─── FORMATTERS ───────────────────────────────────────────────────────────────
const fmt = (value, format) => {
  if (value == null) return '—';
  if (format === 'currency') {
    if (value >= 10000000) return `₹${(value/10000000).toFixed(1)}Cr`;
    if (value >= 100000)   return `₹${(value/100000).toFixed(1)}L`;
    if (value >= 1000)     return `₹${(value/1000).toFixed(1)}K`;
    return `₹${value.toLocaleString()}`;
  }
  if (format === 'percent') return `${value.toFixed(1)}%`;
  if (format === 'ratio')   return `${value.toFixed(1)} / 5`;
  if (value >= 1000000) return `${(value/1000000).toFixed(1)}M`;
  if (value >= 1000)    return `${(value/1000).toFixed(1)}K`;
  return value.toLocaleString();
};

const pct = (curr, prev) => (!prev ? 0 : ((curr - prev) / prev) * 100);

// ─── SPARKLINE ────────────────────────────────────────────────────────────────
const Spark = ({ color }) => {
  const pts = [30,50,40,65,45,70,55,80,60,75,85,72].map((y,i) => `${i*10},${100-y}`).join(' ');
  return (
    <svg viewBox="0 0 110 100" style={{ width:'100%', height:30, display:'block', marginTop:4 }} preserveAspectRatio="none">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.45"/>
    </svg>
  );
};

// ─── KPI CARD ─────────────────────────────────────────────────────────────────
const KPICard = ({ card, period, onClick, onRemove, editMode, dragHandleProps, isDragging }) => {
  const kpi = KPI_LIBRARY.find(k => k.code === card.kpiCode);
  if (!kpi) return null;
  const data    = MOCK_DATA[kpi.code] || {};
  const change  = pct(data.value, data.prev);
  const isGood  = kpi.good === 'up' ? change >= 0 : change <= 0;
  const cc      = change === 0 ? '#94a3b8' : isGood ? '#10b981' : '#ef4444';
  const hasReport = !!card.reportId;

  return (
    <div onClick={() => !editMode && hasReport && onClick()}
      style={{
        background:'#fff', borderRadius:14,
        border:'1px solid #e5e7eb', borderTop:`3px solid ${kpi.color}`,
        padding:'10px 12px 8px',
        cursor: editMode ? 'default' : hasReport ? 'pointer' : 'default',
        display:'flex', flexDirection:'column', gap:2,
        boxShadow: isDragging ? '0 12px 32px rgba(0,0,0,0.15)' : '0 1px 3px rgba(0,0,0,0.05)',
        transform: isDragging ? 'rotate(2deg) scale(1.03)' : 'none',
        transition:'box-shadow 0.2s, transform 0.2s',
        userSelect:'none', position:'relative',
        opacity: !editMode && !hasReport ? 0.75 : 1,
      }}
    >
      {/* Drag + remove row */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', minHeight:16 }}>
        <span {...dragHandleProps} onClick={e=>e.stopPropagation()}
          style={{ cursor:editMode?'grab':'default', color:'#d1d5db', fontSize:13, padding:'1px 4px 1px 0' }}>
          {editMode ? '⠿' : ''}
        </span>
        {editMode && (
          <button onClick={e=>{e.stopPropagation();onRemove();}}
            style={{ background:'none', border:'none', color:'#d1d5db', cursor:'pointer', fontSize:12, padding:2, lineHeight:1 }}>✕</button>
        )}
      </div>

      {/* Icon + name */}
      <div style={{ display:'flex', alignItems:'center', gap:5 }}>
        <span style={{ fontSize:14 }}>{kpi.icon}</span>
        <span style={{ fontSize:10, fontWeight:600, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.5px', fontFamily:'DM Sans,sans-serif' }}>
          {kpi.name}
        </span>
      </div>

      {/* Value */}
      <div style={{ fontFamily:'DM Mono, monospace', fontWeight:500, fontSize:24, color:'#0f172a', lineHeight:1.1, marginTop:2 }}>
        {fmt(data.value, kpi.format)}
      </div>

      {/* Change */}
      <span style={{ fontSize:10, fontWeight:600, color:cc, background:`${cc}18`, padding:'2px 7px', borderRadius:20, alignSelf:'flex-start', marginTop:2 }}>
        {change >= 0 ? '▲' : '▼'} {Math.abs(change).toFixed(1)}% vs prev
      </span>

      <Spark color={kpi.color} />

      {/* Linked report tag */}
      <div style={{ fontSize:9, color: hasReport ? '#3b82f6' : '#cbd5e1', marginTop:2, display:'flex', alignItems:'center', gap:3, fontFamily:'DM Sans,sans-serif' }}>
        <span>{hasReport ? '📋' : '🔗'}</span>
        <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:130 }}>
          {hasReport ? card.reportTitle : 'No report linked'}
        </span>
      </div>

      <div style={{ fontSize:9, color:'#b0bec5', textTransform:'uppercase', letterSpacing:'0.4px', marginTop:1, fontFamily:'DM Sans,sans-serif' }}>{period}</div>
    </div>
  );
};

// ─── ADD KPI MODAL — 2-step ───────────────────────────────────────────────────
const AddKPIModal = ({ activeKpiCodes, reports, onAdd, onClose }) => {
  const [step,           setStep]           = useState(1);
  const [kpiSearch,      setKpiSearch]      = useState('');
  const [cat,            setCat]            = useState('All');
  const [selectedKpi,    setSelectedKpi]    = useState(null);
  const [reportSearch,   setReportSearch]   = useState('');
  const [selectedReport, setSelectedReport] = useState(null); // null = no report

  const cats = ['All', ...Array.from(new Set(KPI_LIBRARY.map(k => k.category)))];

  const availableKpis = KPI_LIBRARY.filter(k =>
    !activeKpiCodes.includes(k.code) &&
    (cat === 'All' || k.category === cat) &&
    k.name.toLowerCase().includes(kpiSearch.toLowerCase())
  );

  const flatReports = [
    ...reports,
    ...reports.flatMap(r => r.children || [])
  ].filter(r => r.title || r.name)
   .filter(r => (r.title || r.name || '').toLowerCase().includes(reportSearch.toLowerCase()));

  const confirm = () => {
    onAdd({
      kpiCode:     selectedKpi.code,
      reportId:    selectedReport ? (selectedReport.reportId || selectedReport.id || null) : null,
      reportTitle: selectedReport ? (selectedReport.title || selectedReport.name || null)  : null,
      datasetId:   selectedReport?.datasetId  || null,
      isSecure:    selectedReport?.isSecure   || false,
      rlsRole:     selectedReport?.rlsRole    || null,
      embedUrl:    selectedReport?.embedURL   || selectedReport?.embed_url || null,
    });
    onClose();
  };

  const S = { // shared styles
    modal:     { background:'#fff', borderRadius:20, width:'100%', maxWidth:480, maxHeight:'88vh', display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'0 24px 60px rgba(0,0,0,0.22)' },
    overlay:   { position:'fixed', inset:0, background:'rgba(15,23,42,0.65)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 },
    input:     { width:'100%', padding:'8px 12px', borderRadius:10, border:'1px solid #e2e8f0', fontSize:13, fontFamily:'DM Sans,sans-serif', outline:'none', boxSizing:'border-box' },
    listWrap:  { overflowY:'auto', padding:'10px 20px 16px', display:'flex', flexDirection:'column', gap:6 },
    row:       { display:'flex', alignItems:'center', gap:10, padding:'10px 12px', borderRadius:12, border:'1px solid #f1f5f9', cursor:'pointer', transition:'background 0.15s' },
  };

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={e=>e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding:'16px 20px 12px', borderBottom:'1px solid #f1f5f9' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
            <div style={{ fontFamily:'Syne,sans-serif', fontWeight:800, fontSize:16, color:'#0f172a' }}>
              {step === 1 ? '① Choose a KPI' : '② Link a Report'}
            </div>
            <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', fontSize:17, color:'#94a3b8', lineHeight:1 }}>✕</button>
          </div>

          {/* Step bar */}
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            {[1,2].map(s => (
              <React.Fragment key={s}>
                <div style={{
                  width:24, height:24, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:11, fontWeight:700, fontFamily:'DM Sans,sans-serif',
                  background: step >= s ? '#3b82f6' : '#f1f5f9',
                  color:      step >= s ? '#fff'    : '#94a3b8',
                }}>{s}</div>
                {s < 2 && <div style={{ flex:1, height:2, borderRadius:2, background: step > s ? '#3b82f6' : '#e2e8f0' }}/>}
              </React.Fragment>
            ))}
            <span style={{ fontSize:11, color:'#94a3b8', marginLeft:4, fontFamily:'DM Sans,sans-serif' }}>
              {step === 1 ? 'Select metric' : 'Select report (optional)'}
            </span>
          </div>
        </div>

        {/* ── STEP 1 ── */}
        {step === 1 && <>
          <div style={{ padding:'10px 20px 8px', borderBottom:'1px solid #fafafa' }}>
            <input autoFocus style={S.input} placeholder="Search KPIs..." value={kpiSearch} onChange={e=>setKpiSearch(e.target.value)}/>
            <div style={{ display:'flex', gap:5, flexWrap:'wrap', marginTop:8 }}>
              {cats.map(c => (
                <button key={c} onClick={()=>setCat(c)} style={{
                  padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:600, border:'none', cursor:'pointer',
                  background: cat===c ? '#3b82f6' : '#f1f5f9',
                  color:      cat===c ? '#fff'    : '#64748b',
                  fontFamily:'DM Sans,sans-serif'
                }}>{c}</button>
              ))}
            </div>
          </div>
          <div style={S.listWrap}>
            {availableKpis.length === 0 && <div style={{ textAlign:'center', color:'#94a3b8', padding:20, fontSize:13 }}>No KPIs available</div>}
            {availableKpis.map(kpi => (
              <div key={kpi.code} style={S.row}
                onClick={()=>{ setSelectedKpi(kpi); setStep(2); }}
                onMouseEnter={e=>e.currentTarget.style.background='#f8fafc'}
                onMouseLeave={e=>e.currentTarget.style.background='#fff'}
              >
                <div style={{ width:36, height:36, borderRadius:10, background:`${kpi.color}15`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 }}>{kpi.icon}</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:600, fontSize:13, color:'#0f172a', fontFamily:'DM Sans,sans-serif' }}>{kpi.name}</div>
                  <div style={{ fontSize:11, color:'#94a3b8', fontFamily:'DM Sans,sans-serif' }}>{kpi.category}</div>
                </div>
                <span style={{ color:'#3b82f6', fontSize:15, fontWeight:700 }}>›</span>
              </div>
            ))}
          </div>
        </>}

        {/* ── STEP 2 ── */}
        {step === 2 && <>
          {/* Selected KPI pill */}
          <div style={{ padding:'8px 20px', background:'#f8fafc', borderBottom:'1px solid #f1f5f9', display:'flex', alignItems:'center', gap:8 }}>
            <div style={{ width:28, height:28, borderRadius:8, background:`${selectedKpi.color}15`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:15 }}>{selectedKpi.icon}</div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:10, color:'#94a3b8', fontFamily:'DM Sans,sans-serif' }}>KPI Selected</div>
              <div style={{ fontSize:13, fontWeight:600, color:'#0f172a', fontFamily:'DM Sans,sans-serif' }}>{selectedKpi.name}</div>
            </div>
            <button onClick={()=>{setStep(1);setSelectedReport(null);}} style={{ fontSize:11, color:'#3b82f6', background:'none', border:'none', cursor:'pointer', fontWeight:600, fontFamily:'DM Sans,sans-serif' }}>← Change</button>
          </div>

          <div style={{ padding:'10px 20px 8px', borderBottom:'1px solid #fafafa' }}>
            <input autoFocus style={S.input} placeholder="Search reports..." value={reportSearch} onChange={e=>setReportSearch(e.target.value)}/>
            <div style={{ fontSize:11, color:'#94a3b8', marginTop:6, fontFamily:'DM Sans,sans-serif' }}>
              Which Power BI report should open when this card is clicked?
            </div>
          </div>

          <div style={S.listWrap}>
            {/* No report option */}
            {[null, ...flatReports].map((r, i) => {
              const isNone = r === null;
              const id     = isNone ? '__none__' : (r.reportId || r.id);
              const title  = isNone ? 'KPI only — no report' : (r.title || r.name);
              const isSel  = isNone ? selectedReport === null : (selectedReport?.reportId === id || selectedReport?.id === id);
              return (
                <div key={id || i} style={{
                  ...S.row,
                  border: isSel ? '2px solid #3b82f6' : '1px solid #f1f5f9',
                  background: isSel ? '#eff6ff' : '#fff',
                }}
                  onClick={()=>setSelectedReport(isNone ? null : r)}
                  onMouseEnter={e=>{ if(!isSel) e.currentTarget.style.background='#f8fafc'; }}
                  onMouseLeave={e=>{ if(!isSel) e.currentTarget.style.background= isSel?'#eff6ff':'#fff'; }}
                >
                  <div style={{ width:34, height:34, borderRadius:9, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:16,
                    background: isNone ? '#f1f5f9' : r?.isSecure ? '#fef3c7' : '#f0fdf4'
                  }}>
                    {isNone ? '⏭️' : r?.isSecure ? '🔒' : '📊'}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:600, fontSize:12, color:'#0f172a', fontFamily:'DM Sans,sans-serif', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{title}</div>
                    <div style={{ fontSize:10, color:'#94a3b8', fontFamily:'DM Sans,sans-serif' }}>
                      {isNone ? 'Card shows KPI value only' : r?.isSecure ? '🔐 Secure · RLS' : '🌐 Public · Power BI'}
                    </div>
                  </div>
                  {isSel && <span style={{ color:'#3b82f6', fontSize:14, flexShrink:0 }}>✓</span>}
                </div>
              );
            })}
          </div>

          {/* Confirm */}
          <div style={{ padding:'10px 20px 16px', borderTop:'1px solid #f1f5f9' }}>
            <button onClick={confirm} style={{
              width:'100%', padding:'11px', borderRadius:12, border:'none',
              background:'#3b82f6', color:'#fff', fontWeight:700, fontSize:13,
              cursor:'pointer', fontFamily:'DM Sans,sans-serif',
              display:'flex', alignItems:'center', justifyContent:'center', gap:8
            }}>
              {selectedKpi.icon} Add "{selectedKpi.name}" to Home Screen
              {selectedReport && <span style={{ fontSize:11, opacity:0.75 }}>→ {selectedReport.title||selectedReport.name}</span>}
            </button>
          </div>
        </>}

      </div>
    </div>
  );
};

// ─── MY REPORT CARD (fixed, always first) ────────────────────────────────────
const MyReportCard = ({ myReport, onOpen, onConfigure, editMode }) => {
  const hasReport = !!myReport.reportId;
  return (
    <div style={{
      gridColumn: 'span 2',
      background: hasReport
        ? 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)'
        : '#f8fafc',
      borderRadius: 14,
      border: hasReport ? 'none' : '2px dashed #cbd5e1',
      padding: '16px 20px',
      cursor: hasReport ? 'pointer' : 'default',
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      minHeight: 140,
      position: 'relative',
      overflow: 'hidden',
      transition: 'box-shadow 0.2s',
      boxShadow: hasReport ? '0 4px 20px rgba(15,23,42,0.25)' : 'none',
    }}
      onClick={() => hasReport && onOpen()}
      onMouseEnter={e => { if(hasReport) e.currentTarget.style.boxShadow='0 8px 32px rgba(15,23,42,0.35)'; }}
      onMouseLeave={e => { if(hasReport) e.currentTarget.style.boxShadow='0 4px 20px rgba(15,23,42,0.25)'; }}
    >
      {/* Decorative circles */}
      {hasReport && <>
        <div style={{ position:'absolute', width:120, height:120, borderRadius:'50%', border:'1px solid rgba(255,255,255,0.06)', top:-30, right:-30, pointerEvents:'none' }}/>
        <div style={{ position:'absolute', width:80, height:80, borderRadius:'50%', border:'1px solid rgba(255,255,255,0.06)', top:10, right:10, pointerEvents:'none' }}/>
      </>}

      {/* Fixed label */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ fontSize:14 }}>👤</span>
          <span style={{ fontSize:10, fontWeight:600, letterSpacing:'0.7px', textTransform:'uppercase', fontFamily:'DM Sans,sans-serif', color: hasReport ? 'rgba(255,255,255,0.5)' : '#94a3b8' }}>
            My Report
          </span>
          <span style={{ fontSize:9, padding:'2px 6px', borderRadius:20, fontFamily:'DM Sans,sans-serif', fontWeight:600,
            background: hasReport ? 'rgba(255,255,255,0.1)' : '#e2e8f0',
            color: hasReport ? 'rgba(255,255,255,0.6)' : '#94a3b8'
          }}>FIXED</span>
        </div>
        {/* Configure button — always visible */}
        <button
          onClick={e => { e.stopPropagation(); onConfigure(); }}
          style={{ fontSize:10, padding:'3px 10px', borderRadius:20, border:'none', cursor:'pointer', fontFamily:'DM Sans,sans-serif', fontWeight:600,
            background: hasReport ? 'rgba(255,255,255,0.12)' : '#e2e8f0',
            color: hasReport ? 'rgba(255,255,255,0.7)' : '#64748b'
          }}
        >{hasReport ? '⚙ Change' : '+ Set Report'}</button>
      </div>

      {/* Report title or empty state */}
      {hasReport ? <>
        <div style={{ fontFamily:'DM Mono, monospace', fontWeight:500, fontSize:18, color:'#fff', lineHeight:1.2, marginTop:4 }}>
          {myReport.reportTitle || 'My Dashboard'}
        </div>
        <div style={{ fontSize:10, color:'rgba(255,255,255,0.4)', fontFamily:'DM Sans,sans-serif' }}>
          {myReport.isSecure ? '🔐 Secure report' : '🌐 Report'} · Click to open
        </div>
        {myReport.lastViewed && (
          <div style={{ fontSize:9, color:'rgba(255,255,255,0.3)', fontFamily:'DM Sans,sans-serif', marginTop:2 }}>
            Last viewed: {myReport.lastViewed}
          </div>
        )}
        <div style={{ position:'absolute', bottom:14, right:16, fontSize:16, color:'rgba(255,255,255,0.2)' }}>→</div>
      </> : <>
        <div style={{ fontSize:13, color:'#94a3b8', fontFamily:'DM Sans,sans-serif', marginTop:4 }}>
          No report linked yet
        </div>
        <div style={{ fontSize:11, color:'#cbd5e1', fontFamily:'DM Sans,sans-serif' }}>
          Click "+ Set Report" to link your Power BI dashboard
        </div>
      </>}
    </div>
  );
};

// ─── MY REPORT PICKER MODAL ───────────────────────────────────────────────────
const MyReportPicker = ({ reports, current, onSelect, onClose }) => {
  const [search, setSearch] = useState('');
  const flatReports = [...reports, ...reports.flatMap(r => r.children || [])]
    .filter(r => r.title || r.name)
    .filter(r => (r.title||r.name||'').toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(15,23,42,0.65)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
      onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:20, width:'100%', maxWidth:460, maxHeight:'80vh', display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'0 24px 60px rgba(0,0,0,0.22)' }}
        onClick={e=>e.stopPropagation()}>

        <div style={{ padding:'16px 20px 12px', borderBottom:'1px solid #f1f5f9' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
            <div>
              <div style={{ fontFamily:'Syne,sans-serif', fontWeight:800, fontSize:16, color:'#0f172a' }}>Set My Report</div>
              <div style={{ fontSize:11, color:'#94a3b8', fontFamily:'DM Sans,sans-serif', marginTop:2 }}>This card is fixed and always shown first</div>
            </div>
            <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', fontSize:17, color:'#94a3b8' }}>✕</button>
          </div>
          <input autoFocus placeholder="Search reports..."
            value={search} onChange={e=>setSearch(e.target.value)}
            style={{ width:'100%', padding:'8px 12px', borderRadius:10, border:'1px solid #e2e8f0', fontSize:13, fontFamily:'DM Sans,sans-serif', outline:'none', boxSizing:'border-box' }}
          />
        </div>

        <div style={{ overflowY:'auto', padding:'10px 20px 16px', display:'flex', flexDirection:'column', gap:6 }}>
          {flatReports.map((r,i) => {
            const id    = r.reportId || r.id;
            const title = r.title    || r.name;
            const isSel = current?.reportId === id || current?.id === id;
            return (
              <div key={id||i}
                onClick={() => { onSelect(r); onClose(); }}
                style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', borderRadius:12,
                  border: isSel ? '2px solid #0f172a' : '1px solid #f1f5f9',
                  background: isSel ? '#f8fafc' : '#fff',
                  cursor:'pointer', transition:'background 0.15s'
                }}
                onMouseEnter={e=>{ if(!isSel) e.currentTarget.style.background='#f8fafc'; }}
                onMouseLeave={e=>{ if(!isSel) e.currentTarget.style.background='#fff'; }}
              >
                <div style={{ width:34, height:34, borderRadius:9, display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0,
                  background: r.isSecure ? '#fef3c7' : '#f0fdf4'
                }}>
                  {r.isSecure ? '🔒' : '📊'}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:600, fontSize:13, color:'#0f172a', fontFamily:'DM Sans,sans-serif', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{title}</div>
                  <div style={{ fontSize:11, color:'#94a3b8', fontFamily:'DM Sans,sans-serif' }}>{r.isSecure ? '🔐 Secure · RLS' : '🌐 Public'} · Power BI</div>
                </div>
                {isSel && <span style={{ color:'#0f172a', fontSize:14 }}>✓</span>}
              </div>
            );
          })}
          {flatReports.length === 0 && <div style={{ textAlign:'center', color:'#94a3b8', padding:24, fontSize:13 }}>No reports found</div>}
        </div>
      </div>
    </div>
  );
};

// ─── HOME SCREEN ──────────────────────────────────────────────────────────────
const Dashboard1 = () => {
  const history  = useHistory();
  const dispatch = useDispatch();
  const account  = useSelector(s => s.account);
  const orgId    = useSelector(s => s?.account?.org?.id);
  const userId   = useSelector(s => s?.account?.org_member?.id);

  const [cards,      setCards]      = useState([
    { kpiCode:'TOTAL_REVENUE',       reportId:null, reportTitle:null, datasetId:null, isSecure:false, rlsRole:null, embedUrl:null },
    { kpiCode:'GROSS_PROFIT',        reportId:null, reportTitle:null, datasetId:null, isSecure:false, rlsRole:null, embedUrl:null },
    { kpiCode:'NET_PROFIT',          reportId:null, reportTitle:null, datasetId:null, isSecure:false, rlsRole:null, embedUrl:null },
    { kpiCode:'GROSS_MARGIN_PCT',    reportId:null, reportTitle:null, datasetId:null, isSecure:false, rlsRole:null, embedUrl:null },
    { kpiCode:'TOTAL_COST_OF_SALES', reportId:null, reportTitle:null, datasetId:null, isSecure:false, rlsRole:null, embedUrl:null },
    { kpiCode:'RECEIVABLES',         reportId:null, reportTitle:null, datasetId:null, isSecure:false, rlsRole:null, embedUrl:null },
    { kpiCode:'WORKING_CAPITAL',     reportId:null, reportTitle:null, datasetId:null, isSecure:false, rlsRole:null, embedUrl:null },
    { kpiCode:'PRODUCTION_QTY',      reportId:null, reportTitle:null, datasetId:null, isSecure:false, rlsRole:null, embedUrl:null },
    { kpiCode:'TOTAL_SALARY',        reportId:null, reportTitle:null, datasetId:null, isSecure:false, rlsRole:null, embedUrl:null },
    { kpiCode:'FINISHED_GOODS',      reportId:null, reportTitle:null, datasetId:null, isSecure:false, rlsRole:null, embedUrl:null },
  ]);

  const [period,     setPeriod]     = useState('QTD');
  const [editMode,   setEditMode]   = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [reports,    setReports]    = useState([]);
  const [myReport,   setMyReport]   = useState({ reportId:null, reportTitle:null, datasetId:null, isSecure:false, rlsRole:null, embedUrl:null, lastViewed:null });
  const [showMyReportPicker, setShowMyReportPicker] = useState(false);
  const [dragIdx,    setDragIdx]    = useState(null);
  const [dragOver,   setDragOver]   = useState(null);

  // Fetch PBI reports
  useEffect(() => {
    const load = async () => {
      try {
        let res;
        if (account.role === 'Owner') {
          res = await axiosInstance.get(`${configData.API_SERVER}/user/get_all_reports`);
        } else if (account.role === 'Super Admin' && account.org !== null) {
          res = await axiosInstance.get(`${configData.API_SERVER}/user/allocated-reports?org_id=${account.org.id}`);
        } else {
          const u = await axiosInstance.get(`${configData.API_SERVER}/user/get_user_with_email?email=${account?.org_member?.email}`);
          res = await axiosInstance.get(`${configData.API_SERVER}/user/allocated-reports?user_id=${u.data.id}`);
        }
        setReports(res?.data || []);
      } catch (e) { console.error(e); }
    };
    load();
  }, [account]);

  // ── LOAD cockpit config on mount ──
  useEffect(() => {
    if (!orgId || !userId) return;
    axiosInstance.get(`${configData.API_SERVER}/user/cockpit-config/`, {
      params: { org_id: orgId, user_id: userId }
    }).then(res => {
      if (res.data.my_report?.reportId) setMyReport(res.data.my_report);
      if (res.data.kpi_cards?.length > 0) setCards(res.data.kpi_cards);
      if (res.data.period) setPeriod(res.data.period);
    }).catch(e => console.error('Failed to load cockpit config', e));
  }, [orgId, userId]);

  // ── SAVE cockpit config on every change (debounced 800ms) ──
  useEffect(() => {
    if (!orgId || !userId) return;
    const timer = setTimeout(() => {
      axiosInstance.post(`${configData.API_SERVER}/user/cockpit-config/`, {
        org_id:    orgId,
        user_id:   userId,
        my_report: myReport,
        kpi_cards: cards,
        period:    period,
      }).catch(e => console.error('Failed to save cockpit config', e));
    }, 800);
    return () => clearTimeout(timer);
  }, [cards, myReport, period]);

  function getCookie(n) {
    let v = null;
    if (document.cookie) document.cookie.split(';').forEach(c => { const t=c.trim(); if(t.startsWith(`${n}=`)) v=decodeURIComponent(t.slice(n.length+1)); });
    return v;
  }

  const handleMyReportClick = async () => {
    if (!myReport.reportId) return;
    const fullReport =
      reports.find(r => r.id===myReport.reportId||r.reportId===myReport.reportId) ||
      reports.flatMap(r=>r.children||[]).find(c=>c.id===myReport.reportId||c.reportId===myReport.reportId);
    try {
      if (myReport.isSecure) {
        const csrf = getCookie('csrftoken');
        const tr   = await axiosInstance.post(`${configData.API_SERVER}/user/get-powerbi-embed-token`,
          { reportId:myReport.reportId, datasetId:myReport.datasetId, orgId, rlsRole:myReport.rlsRole },
          { headers:{ 'Content-Type':'application/json','X-CSRFToken':csrf } }
        );
        const { embedToken, embedUrl, groupId, datasetId:dsId } = tr.data;
        dispatch({ type:SET_REPORT_DATA, payload:{ reportData:embedUrl||fullReport?.embedURL, embedUrl:embedUrl||fullReport?.embedURL, title:myReport.reportTitle, isSecure:true, reportId:myReport.reportId, datasetId:dsId, groupId, accessToken:embedToken }});
      } else {
        dispatch({ type:SET_REPORT_DATA, payload:{ reportData:myReport.embedUrl||fullReport?.embedURL, embedUrl:myReport.embedUrl||fullReport?.embedURL, title:myReport.reportTitle, isSecure:false }});
      }
      setMyReport(prev => ({ ...prev, lastViewed: new Date().toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) }));
      history.push('/power-bi-report-page');
    } catch(e) { console.error(e); }
  };

  const handleMyReportSelect = (r) => {
    setMyReport({
      reportId:    r.reportId || r.id || null,
      reportTitle: r.title    || r.name || null,
      datasetId:   r.datasetId  || null,
      isSecure:    r.isSecure   || false,
      rlsRole:     r.rlsRole    || null,
      embedUrl:    r.embedURL   || r.embed_url || null,
      lastViewed:  null,
    });
  };

  const handleCardClick = async (card) => {
    if (!card.reportId) return;
    const fullReport =
      reports.find(r => r.id===card.reportId||r.reportId===card.reportId) ||
      reports.flatMap(r=>r.children||[]).find(c=>c.id===card.reportId||c.reportId===card.reportId);
    try {
      if (card.isSecure) {
        const csrf = getCookie('csrftoken');
        const tr   = await axiosInstance.post(`${configData.API_SERVER}/user/get-powerbi-embed-token`,
          { reportId:card.reportId, datasetId:card.datasetId, orgId, rlsRole:card.rlsRole },
          { headers:{ 'Content-Type':'application/json','X-CSRFToken':csrf } }
        );
        const { embedToken, embedUrl, groupId, datasetId:dsId } = tr.data;
        dispatch({ type:SET_REPORT_DATA, payload:{ reportData:embedUrl||fullReport?.embedURL, embedUrl:embedUrl||fullReport?.embedURL, title:card.reportTitle, isSecure:true, reportId:card.reportId, datasetId:dsId, groupId, accessToken:embedToken }});
      } else {
        dispatch({ type:SET_REPORT_DATA, payload:{ reportData:card.embedUrl||fullReport?.embedURL, embedUrl:card.embedUrl||fullReport?.embedURL, title:card.reportTitle, isSecure:false }});
      }
      history.push('/power-bi-report-page');
    } catch(e) { console.error(e); }
  };

  const onDragStart = i => setDragIdx(i);
  const onDragOver  = (e,i) => { e.preventDefault(); setDragOver(i); };
  const onDrop      = () => {
    if (dragIdx===null||dragOver===null||dragIdx===dragOver) { setDragIdx(null);setDragOver(null);return; }
    const a=[...cards]; const [m]=a.splice(dragIdx,1); a.splice(dragOver,0,m); setCards(a); setDragIdx(null);setDragOver(null);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600&family=DM+Mono:wght@400;500&display=swap');
        *{box-sizing:border-box}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:.3}}
        .hs-card:hover{box-shadow:0 6px 20px rgba(0,0,0,.09)!important;transform:translateY(-2px)!important}
      `}</style>

      <div style={{ minHeight:'100vh', fontFamily:'DM Sans,sans-serif' }}>
        
        {/* CONTROLS BAR */}
        <div style={{ padding:'10px 20px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>

          {/* Period selector */}
          <div style={{ display:'flex', background:'#f1f5f9', borderRadius:10, padding:3, gap:2 }}>
            {['MTD','QTD','YTD','FY'].map(p=>(
              <button key={p} onClick={()=>setPeriod(p)} style={{
                padding:'5px 12px', borderRadius:8, border:'none', cursor:'pointer',
                background: period===p ? '#3b82f6' : 'none',
                color: period===p ? '#fff' : '#64748b',
                fontWeight:600, fontSize:12, fontFamily:'DM Sans,sans-serif', transition:'all .2s'
              }}>{p}</button>
            ))}
          </div>

          {/* Actions */}
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <button onClick={()=>setEditMode(e=>!e)} style={{
              padding:'6px 14px', borderRadius:9,
              border: `1.5px solid ${editMode ? '#3b82f6' : '#e2e8f0'}`,
              background: editMode ? '#eff6ff' : '#fff',
              color: editMode ? '#3b82f6' : '#64748b',
              fontWeight:600, fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif'
            }}>{editMode ? '✅ Done' : '✏️ Customise'}</button>

            {editMode && (
              <button onClick={()=>setShowPicker(true)} style={{
                padding:'6px 14px', borderRadius:9, border:'none',
                background:'#3b82f6', color:'#fff',
                fontWeight:600, fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif'
              }}>+ Add KPI</button>
            )}
          </div>

        </div>
        {/* CONTENT */}
        <div style={{ padding:'14px 20px', maxWidth:1400, margin:'0 auto' }}>

          {/* Page title */}
          <div style={{ marginBottom:12,display:'flex',alignItems:'baseline',justifyContent:'space-between' }}>
            <div>
              <h1 style={{ fontFamily:'Syne,sans-serif',fontWeight:800,fontSize:20,color:'#0f172a',margin:0 }}>My Cockpit</h1>
              <p style={{ color:'#94a3b8',fontSize:11,margin:'2px 0 0',fontFamily:'DM Sans,sans-serif' }}>
                {cards.length} metrics · {period} · Live <span style={{ display:'inline-block',width:6,height:6,borderRadius:'50%',background:'#10b981',marginLeft:5,verticalAlign:'middle',animation:'pulse 1.5s infinite' }}/>
              </p>
            </div>
            {editMode && <span style={{ fontSize:11,color:'#94a3b8',fontStyle:'italic',fontFamily:'DM Sans,sans-serif' }}>Drag ⠿ to reorder · ✕ to remove</span>}
          </div>

          {/* KPI GRID — auto-fill keeps it one screen on desktop & iPad */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(162px,1fr))', gap:12, marginBottom:14 }}>

            {/* FIXED FIRST CARD — My Report */}
            <MyReportCard
              myReport={myReport}
              onOpen={handleMyReportClick}
              onConfigure={() => setShowMyReportPicker(true)}
              editMode={editMode}
            />
            {cards.map((card,idx)=>(
              <div key={card.kpiCode} className="hs-card"
                draggable={editMode}
                onDragStart={()=>onDragStart(idx)}
                onDragOver={e=>onDragOver(e,idx)}
                onDrop={onDrop}
                onDragEnd={()=>{setDragIdx(null);setDragOver(null);}}
                style={{ opacity:dragIdx===idx ? 0.4 : 1, outline:dragOver===idx&&dragIdx!==idx?'2px dashed #3b82f6':'none', borderRadius:14, transition:'opacity .15s' }}
              >
                <KPICard
                  card={card} period={period}
                  onClick={()=>handleCardClick(card)}
                  onRemove={()=>setCards(p=>p.filter(c=>c.kpiCode!==card.kpiCode))}
                  editMode={editMode}
                  isDragging={dragIdx===idx}
                  dragHandleProps={editMode?{draggable:true,onDragStart:e=>{e.stopPropagation();onDragStart(idx);}}:{}}
                />
              </div>
            ))}

            {editMode && (
              <div onClick={()=>setShowPicker(true)} style={{ borderRadius:14,border:'2px dashed #cbd5e1',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:140,cursor:'pointer',color:'#94a3b8',gap:4,transition:'all .2s' }}
                onMouseEnter={e=>{e.currentTarget.style.borderColor='#3b82f6';e.currentTarget.style.color='#3b82f6';}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor='#cbd5e1';e.currentTarget.style.color='#94a3b8';}}>
                <span style={{ fontSize:24 }}>+</span>
                <span style={{ fontSize:11,fontWeight:600,fontFamily:'DM Sans,sans-serif' }}>Add KPI</span>
              </div>
            )}
          </div>

          {/* UTILITY TILES */}
          <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))',gap:10 }}>
            {[
              {label:'Business Consultant', icon:'🤖',path:'/chat-agent', bg:'#fff7ed',color:'#f97316'},
              {label:'Financial Analyst',  icon:'📊',path:'/spreadsheet-agent',  bg:'#f0fdf4',color:'#10b981'},
              {label:'Settings',         icon:'⚙️',path:'/settings',         bg:'#faf5ff',color:'#8b5cf6'},
            ].map(({label,icon,path,bg,color})=>(
              <div key={label} onClick={()=>history.push(path)} style={{ background:bg,borderRadius:12,border:`1px solid ${color}22`,padding:'12px',cursor:'pointer',display:'flex',alignItems:'center',gap:8,transition:'transform .15s,box-shadow .15s' }}
                onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow='0 4px 12px rgba(0,0,0,.08)';}}
                onMouseLeave={e=>{e.currentTarget.style.transform='none';e.currentTarget.style.boxShadow='none';}}>
                <span style={{fontSize:17}}>{icon}</span>
                <span style={{fontSize:11,fontWeight:600,color,fontFamily:'DM Sans,sans-serif'}}>{label}</span>
              </div>
            ))}
          </div>

        </div>
      </div>

      {showPicker && (
        <AddKPIModal
          activeKpiCodes={cards.map(c=>c.kpiCode)}
          reports={reports}
          onAdd={card=>setCards(p=>[...p,card])}
          onClose={()=>setShowPicker(false)}
        />
      )}

      {showMyReportPicker && (
        <MyReportPicker
          reports={reports}
          current={myReport}
          onSelect={handleMyReportSelect}
          onClose={()=>setShowMyReportPicker(false)}
        />
      )}
    </>
  );
};

export default Dashboard1;
